import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import SaveIcon from "@mui/icons-material/Save";

import {
  getAircraftOverview,
  getComplianceWithCourses,
  type ComplianceWithCoursesResponseHydrated as ComplianceWithCourses,
} from "src/api/compliance";
import { getDefaultSettings, saveDefaultSettings } from "src/api/settings";
import Button from "src/components/Button";
import CacheStatusBanner from "src/components/CacheStatusBanner";
import CheckboxAutocomplete from "src/components/CheckboxAutocomplete";
import { createSelectableObject, SelectableObject } from "src/components/Form/types";
import AircraftOverviewTable from "src/components/AircraftOverviewTable";
import { EmptyState } from "src/components/EmptyState";
import ExcelExportButton from "src/components/ExcelExportButton";
import Spinner from "src/components/Spinner";
import DisplayContext from "src/context/DisplayContext";
import { CachePrefix } from "src/shared/api-types/cache";
import type { AircraftOverviewColumn, AircraftOverviewRow } from "src/shared/api-types/certificate";
import { SettingKey } from "src/shared/api-types/user";
import { useCacheRefresh } from "src/hooks/useCacheRefresh";
import useCrew from "src/hooks/useCrew";
import { captureHandledException } from "src/services/sentry";
import { buildContextLine, filterPart, selectedNames, selectedUsersPart } from "src/utils/excelExport";

import AircraftOverviewDetails, { type AircraftOverviewCell } from "./AircraftOverviewDetails";
import { buildAircraftOverviewSheet } from "./excelSheet";

const CACHE_REFRESH_CONFIG = {
  baseUrl: "/compliance",
  cachePrefix: CachePrefix.USER_COMPLIANCE_WITH_COURSES,
} as const;

const fullscreenSx = {
  position: "fixed" as const,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: "100vw",
  height: "100vh",
  backgroundColor: "white",
  overflow: "auto",
  zIndex: 1200,
};

const filtersGridSx = ({ spacing }: { spacing: (value: number) => string }) => ({
  display: "grid",
  gridTemplateColumns: {
    xs: "repeat(1, 1fr)",
    sm: "repeat(2, 1fr)",
    md: "repeat(5, 1fr)",
  },
  gap: "10px",
  paddingTop: spacing(1),
  paddingBottom: spacing(1),
});

const getDataUpdatedSx = (dataUpdated: boolean) => ({
  position: "relative",
  overflow: "hidden",
  "&::after": dataUpdated
    ? {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "3px",
        background: "linear-gradient(90deg, transparent, rgba(33, 150, 243, 0.95), transparent)",
        animation: "sweepDown 1.8s ease-in-out",
        pointerEvents: "none",
        zIndex: 10000,
        "@keyframes sweepDown": {
          "0%": { top: "0%", opacity: 1 },
          "95%": { top: "95%", opacity: 0.8 },
          "100%": { top: "95%", opacity: 0 },
        },
      }
    : {},
});

interface AircraftOverviewProps {
  match: {
    params: {
      crewId: string;
    };
  };
}

const getFilterOptions = (columns: AircraftOverviewColumn[], rows: AircraftOverviewRow[]) => {
  const visibleRoleMap = new Map<number, string>();
  const visiblePositionMap = new Map<number, string>();
  const visibleOperationMap = new Map<number, string>();
  columns.forEach((column) => {
    visibleRoleMap.set(column.roleId, column.roleName);
    visiblePositionMap.set(column.positionId, column.positionName);
    visibleOperationMap.set(column.operationId, column.operationName);
  });

  const visibleAircraftMap = new Map<number, string>();
  rows.forEach((row) => {
    visibleAircraftMap.set(row.aircraftId, row.aircraftName);
  });

  return {
    userOptions: rows
      .filter((row, index, arr) => arr.findIndex((x) => x.userId === row.userId) === index)
      .map((row) => createSelectableObject(row.userId.toString(), row.userName)),
    roleOptions: Array.from(visibleRoleMap.entries())
      .map(([id, name]) => createSelectableObject(id.toString(), name))
      .sort((a, b) => a.getDisplayName().localeCompare(b.getDisplayName())),
    positionOptions: Array.from(visiblePositionMap.entries())
      .map(([id, name]) => createSelectableObject(id.toString(), name))
      .sort((a, b) => a.getDisplayName().localeCompare(b.getDisplayName())),
    operationOptions: Array.from(visibleOperationMap.entries())
      .map(([id, name]) => createSelectableObject(id.toString(), name))
      .sort((a, b) => a.getDisplayName().localeCompare(b.getDisplayName())),
    aircraftOptions: Array.from(visibleAircraftMap.entries())
      .map(([id, name]) => createSelectableObject(id.toString(), name))
      .sort((a, b) => a.getDisplayName().localeCompare(b.getDisplayName())),
  };
};

const AircraftOverview: FC<AircraftOverviewProps> = ({ match }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar: snack } = useSnackbar();
  const { crew } = useCrew();
  const { fullscreen, toggleFullscreen } = useContext(DisplayContext);
  const crewId = Number(match.params.crewId);

  const [columns, setColumns] = useState<AircraftOverviewColumn[]>([]);
  const [rows, setRows] = useState<AircraftOverviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cachePending, setCachePending] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [searchUsers, setSearchUsers] = useState<number[]>([]);
  const [searchRoles, setSearchRoles] = useState<number[]>([]);
  const [searchPositions, setSearchPositions] = useState<number[]>([]);
  const [searchOperations, setSearchOperations] = useState<number[]>([]);
  const [searchAircraft, setSearchAircraft] = useState<number[]>([]);
  const [userOptions, setUserOptions] = useState<SelectableObject[]>([]);
  const [roleOptions, setRoleOptions] = useState<SelectableObject[]>([]);
  const [positionOptions, setPositionOptions] = useState<SelectableObject[]>([]);
  const [operationOptions, setOperationOptions] = useState<SelectableObject[]>([]);
  const [aircraftOptions, setAircraftOptions] = useState<SelectableObject[]>([]);
  // The crew's (large) compliance with courses, for the course details: null while loading
  const [courseData, setCourseData] = useState<Record<string, ComplianceWithCourses> | null>(null);
  const [courseDataFailed, setCourseDataFailed] = useState(false);
  const [detailsCell, setDetailsCell] = useState<AircraftOverviewCell | null>(null);
  // The report on screen once it has loaded; the course data is loaded for exactly this report
  const [loadedReport, setLoadedReport] = useState<{
    crewId: number;
    baseIds: Parameters<typeof getComplianceWithCourses>[2];
    hasStatuses: boolean;
  } | null>(null);

  const { isRefreshing, refreshProgress, oldestCacheTime, refreshKey, dataUpdated, forceRefresh, setOldestCacheTime } =
    useCacheRefresh({
      config: CACHE_REFRESH_CONFIG,
      crewId,
    });
  const forceRefreshRef = useRef(forceRefresh);
  // The crew whose saved default filters have been applied. Tracked per crew, not by refreshKey === 0: a refresh
  // that completes during the first load supersedes that load, and its re-run must still apply the defaults.
  const defaultsAppliedForCrewRef = useRef<number | null>(null);

  useEffect(() => {
    forceRefreshRef.current = forceRefresh;
  }, [forceRefresh]);

  useEffect(() => {
    if (!crewId) return;

    // A response for a crew or refresh that is no longer current must not replace the report, or start the course
    // details fetch for that crew
    let ignore = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        // The report re-renders from new rows, so open details would point at a status that no longer exists
        setDetailsCell(null);
        const needsDefaults = defaultsAppliedForCrewRef.current !== crewId;

        const [overviewResponse, defaultSettings] = await Promise.all([
          getAircraftOverview(crewId, crew?.baseIds),
          needsDefaults ? getDefaultSettings(SettingKey.AIRCRAFT_OVERVIEW) : Promise.resolve(null),
        ]);
        if (ignore) return;

        setOldestCacheTime(overviewResponse.cachedAt || null);

        if (needsDefaults) {
          defaultsAppliedForCrewRef.current = crewId;
          const filters = defaultSettings?.value;
          setSearchUsers(filters?.users ?? []);
          setSearchRoles(filters?.roles ?? []);
          setSearchPositions(filters?.positions ?? []);
          setSearchOperations(filters?.operations ?? []);
          setSearchAircraft(filters?.aircraft ?? []);
        }

        const { columns, rows } = overviewResponse.data;
        const isCacheMissResponse = !overviewResponse.cachedAt && columns.length === 0 && rows.length === 0;
        setCachePending(isCacheMissResponse);
        if (isCacheMissResponse) {
          void forceRefreshRef.current();
        }

        const filterOptions = getFilterOptions(columns, rows);
        setColumns(columns);
        setRows(rows);
        setUserOptions(filterOptions.userOptions);
        setRoleOptions(filterOptions.roleOptions);
        setPositionOptions(filterOptions.positionOptions);
        setOperationOptions(filterOptions.operationOptions);
        setAircraftOptions(filterOptions.aircraftOptions);
        setLoadedReport({ crewId, baseIds: crew?.baseIds, hasStatuses: columns.length > 0 && rows.length > 0 });
      } catch (err) {
        if (ignore) return;
        captureHandledException(err, { feature: "aircraftOverview", action: "load", crewId });
        setError(t("failed_to_load_aircraft_overview"));
      } finally {
        if (!ignore) setLoading(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [crewId, crew?.baseIds, refreshKey, setOldestCacheTime, t]);

  // The course details need the crew's compliance with courses. It is large, so it loads only after a report with
  // statuses to click has loaded (once per load, also after a crew switch or a refresh); details opened before it
  // arrives show a spinner for the courses.
  useEffect(() => {
    if (!loadedReport?.hasStatuses) return;

    const { crewId: reportCrewId, baseIds } = loadedReport;
    let ignore = false;
    setCourseData(null);
    setCourseDataFailed(false);
    getComplianceWithCourses(reportCrewId, true, baseIds)
      .then((response) => {
        if (!ignore) setCourseData(response.data);
      })
      .catch((err) => {
        if (ignore) return;
        captureHandledException(err, {
          feature: "aircraftOverview",
          action: "loadCourseDetails",
          crewId: reportCrewId,
        });
        setCourseDataFailed(true);
      });

    return () => {
      ignore = true;
    };
  }, [loadedReport]);

  const filteredData = useMemo(() => {
    const filteredColumns = columns.filter((column) => {
      if (searchRoles.length > 0 && !searchRoles.includes(column.roleId)) return false;
      if (searchPositions.length > 0 && !searchPositions.includes(column.positionId)) return false;
      if (searchOperations.length > 0 && !searchOperations.includes(column.operationId)) return false;
      return true;
    });

    const filteredRows = rows.filter((row) => {
      if (searchUsers.length > 0 && !searchUsers.includes(row.userId)) return false;
      if (searchAircraft.length > 0 && !searchAircraft.includes(row.aircraftId)) return false;
      return true;
    });

    const visibleColumns = filteredColumns.filter((column) =>
      filteredRows.some((row) => row.operationCompliance[column.key]?.combined.status),
    );
    const visibleColumnKeys = new Set(visibleColumns.map((column) => column.key));
    const visibleRows = filteredRows
      .map((row) => ({
        ...row,
        operationCompliance: Object.fromEntries(
          Object.entries(row.operationCompliance).filter(([key]) => visibleColumnKeys.has(key)),
        ),
      }))
      .filter((row) => Object.values(row.operationCompliance).some((item) => item.combined.status));

    return { columns: visibleColumns, rows: visibleRows };
  }, [columns, rows, searchAircraft, searchOperations, searchPositions, searchRoles, searchUsers]);

  const clearFilters = () => {
    setSearchUsers([]);
    setSearchRoles([]);
    setSearchPositions([]);
    setSearchOperations([]);
    setSearchAircraft([]);
  };

  const hasFilters =
    searchUsers.length > 0 ||
    searchRoles.length > 0 ||
    searchPositions.length > 0 ||
    searchOperations.length > 0 ||
    searchAircraft.length > 0;

  const handleSaveSettings = async () => {
    if (!crewId) return;

    setIsSavingSettings(true);
    try {
      await saveDefaultSettings(SettingKey.AIRCRAFT_OVERVIEW, {
        users: searchUsers,
        roles: searchRoles,
        positions: searchPositions,
        operations: searchOperations,
        aircraft: searchAircraft,
      });
      snack(t("default_filters_saved"), { variant: "success" });
    } catch (err) {
      captureHandledException(err, { feature: "aircraftOverview", action: "saveDefaultFilters", crewId });
      snack(t("default_filters_save_failed"), { variant: "error" });
    } finally {
      setIsSavingSettings(false);
    }
  };

  if (loading) return <Spinner message={t("loading_aircraft_overview")} />;

  if (error) {
    return (
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    );
  }

  if (!columns.length || !rows.length) {
    return (
      <Box>
        {(cachePending || isRefreshing) && (
          <CacheStatusBanner
            cachedAt={oldestCacheTime}
            isRefreshing={cachePending || isRefreshing}
            refreshProgress={refreshProgress}
            dataUpdated={dataUpdated}
            onRefresh={forceRefresh}
          />
        )}
        {!(cachePending || isRefreshing) && <EmptyState />}
      </Box>
    );
  }

  return (
    <Box sx={fullscreen ? fullscreenSx : undefined}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          gap: "10px",
          mb: 1,
        }}
      >
        <CacheStatusBanner
          cachedAt={oldestCacheTime}
          isRefreshing={isRefreshing}
          refreshProgress={refreshProgress}
          dataUpdated={dataUpdated}
          onRefresh={forceRefresh}
        />
        <Box
          sx={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
            flexWrap: "wrap",
            alignSelf: { xs: "flex-end", md: "auto" },
          }}
        >
          <Button
            icon={<FilterAltOffIcon />}
            onClick={clearFilters}
            disabled={!hasFilters}
            iconSize="large"
            title={t("clear_all_filters")}
          />
          <Button
            icon={<SaveIcon />}
            onClick={handleSaveSettings}
            disabled={isSavingSettings}
            iconSize="large"
            title={t("save_default_filters")}
          />
          <Button
            icon={fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            onClick={() => toggleFullscreen()}
            iconSize="large"
            title={t("toggle_fullscreen")}
          />
          <ExcelExportButton
            reportName="aircraft_overview"
            calculatedAt={oldestCacheTime}
            buildExport={() => ({
              contextLine: buildContextLine(t, oldestCacheTime, [
                selectedUsersPart(t, searchUsers.length),
                filterPart(t("filter_roles"), selectedNames(roleOptions, searchRoles)),
                filterPart(t("filter_positions"), selectedNames(positionOptions, searchPositions)),
                filterPart(t("filter_operations"), selectedNames(operationOptions, searchOperations)),
                filterPart(t("compliance.aircraft"), selectedNames(aircraftOptions, searchAircraft)),
              ]),
              sheet: buildAircraftOverviewSheet(filteredData.columns, filteredData.rows, t),
            })}
          />
        </Box>
      </Box>

      <Box sx={filtersGridSx}>
        <CheckboxAutocomplete
          items={userOptions}
          selected={userOptions.filter((opt) => searchUsers.includes(Number(opt.getId())))}
          label={t("filter_users")}
          onChange={(value) => setSearchUsers(value.map((v) => Number(v.getId())))}
        />
        <CheckboxAutocomplete
          items={roleOptions}
          selected={roleOptions.filter((opt) => searchRoles.includes(Number(opt.getId())))}
          label={t("filter_roles")}
          onChange={(value) => setSearchRoles(value.map((v) => Number(v.getId())))}
        />
        <CheckboxAutocomplete
          items={positionOptions}
          selected={positionOptions.filter((opt) => searchPositions.includes(Number(opt.getId())))}
          label={t("filter_positions")}
          onChange={(value) => setSearchPositions(value.map((v) => Number(v.getId())))}
        />
        <CheckboxAutocomplete
          items={operationOptions}
          selected={operationOptions.filter((opt) => searchOperations.includes(Number(opt.getId())))}
          label={t("filter_operations")}
          onChange={(value) => setSearchOperations(value.map((v) => Number(v.getId())))}
        />
        <CheckboxAutocomplete
          items={aircraftOptions}
          selected={aircraftOptions.filter((opt) => searchAircraft.includes(Number(opt.getId())))}
          label={t("compliance.aircraft")}
          onChange={(value) => setSearchAircraft(value.map((v) => Number(v.getId())))}
        />
      </Box>

      <Box sx={getDataUpdatedSx(dataUpdated)}>
        <AircraftOverviewTable
          columns={filteredData.columns}
          rows={filteredData.rows}
          onClearFilters={hasFilters ? clearFilters : undefined}
          onOpenDetails={setDetailsCell}
        />
      </Box>
      {detailsCell && (
        // Keyed per status, so every status opens fresh details (expanded sections, no course history open)
        <AircraftOverviewDetails
          key={`${detailsCell.row.key}-${detailsCell.column.key}`}
          crewId={crewId}
          cell={detailsCell}
          memberCompliance={courseData ? (courseData[detailsCell.row.userId] ?? null) : undefined}
          coursesFailed={courseDataFailed}
          onClose={() => setDetailsCell(null)}
        />
      )}
    </Box>
  );
};

export default AircraftOverview;
